<template>
  <tr 
    v-if="viewPort == 'large'"
    class="sm-customers-table__desktop-item"
    :class="{
      'to-approve': showNoDocumentsWarning && item.required_documents_uploaded
    }"
  >
    <td class="registered sm-txt-nowrap">
      <span v-if="item.registered_on">{{item.registered_on | moment('MMM D, YYYY')}}</span>
    </td>
    <td class="pmb sm-txt-center">
      <span>{{item.pmb}}</span>
      <hint-wrap
        v-if="item.is_active && item.pmb"
        :text="'Fast mail entry'"
        :left="true"
      >
        <template slot="content">
          <a
            href="#"
            class="sm-customers-table__quick-mail"
            @click.prevent="quickMail"
          >
            <include-svg 
              :name="'icon-quick-mail'"
            />
          </a>
        </template>
      </hint-wrap>
    </td>
    <td class="username sm-txt-center">
      <a
        href="#"
        class="sm-link-blue"
        :class="{
          'disabled': item.id != userAccount.id && !checkHigherRole(userAccount.role, item.role)
        }"
        @click.prevent="openAccount(item.id)"
      >{{item.user_name}}</a>
    </td>
    <td class="name">
      <!-- <span>{{item.full_name}}</span> -->
      <text-truncate :text="item.full_name" />
      <hint-wrap
        v-if="warning"
        :text="warning"
        :left="true"
      >
        <template slot="content">
          <a
            href="#"
            @click.prevent="openAccount(item.id, null, true)"
          >
            <include-svg 
              :name="showNoDocumentsWarning && item.required_documents_uploaded ? 'icon-documents-approve' : 'icon-warning'"
            />
          </a>
        </template>
      </hint-wrap>
      <hint-wrap
        v-if="!item.has_card"
        :text="'This customer was enabled by the admin.\n Does not have a credit card on file.'"
        :left="true"
      >
        <template slot="content">
          <include-svg :name="'icon-no-card'" />
        </template>
      </hint-wrap>
      <div
        v-if="item.has_joint_accounts"
        class="sm-customers-table__recipients"
        @mouseenter="showRecipientsList"
        @mouseleave="hideRecipientsList"
      >
        <include-svg :name="'icon-joint-accounts'" />
        <hint-block
          v-if="showRecipients"
          :class="'left'"
        >
          <template slot="hintContent">
            <p class="sm-txt-strong">Additional recipients</p>
            <ul
              v-if="item.joint_accounts && item.joint_accounts.length" 
              class="sm-customers-table__recipients-list"
            >
              <li
                v-for="recip in item.joint_accounts"
                :key="recip.id"
              >
                <include-svg :name="'icon-joint-account'" />
                <span>{{recip.first_name}} {{recip.last_name}}</span>
              </li>
            </ul>
            <sm-spinner
              v-else
              :show="true"
            />
          </template>
        </hint-block>
      </div>
    </td>
    <td class="company">
      <text-truncate :text="item.company_name" />
    </td>
    <td class="plan">
      <p>{{item.plan}}</p>
      <span 
        v-if="item.term && showTerm"
        class="sm-small sm-txt-capitalize disabled"
      >{{item.term.replace('_', '')}}</span>
    </td>
    <td class="registered sm-txt-nowrap">
      <span v-if="item.started_at">{{item.started_at | moment('MMM D, YYYY')}}</span>
    </td>
    <td 
      v-if="$route.query.tab != 'inactive'"
      class="registered sm-txt-nowrap"
    >
      <span v-if="item.renew_on">{{item.renew_on | moment('MMM D, YYYY')}}</span>
    </td>
    <td
      v-if="$route.query.tab && $route.query.tab != 'active'" 
      class="registered sm-txt-nowrap"
    >
      <span v-if="item.ended_at">{{item.ended_at | moment('MMM D, YYYY')}}</span>
    </td>
    <td class="is-active sm-txt-right">
      <div 
        v-if="!item.is_privileged"
        class="sm-customers-table__desktop-item-status"
        :class="{'active': item.is_active}"
      >{{item.is_active ? 'Active' : 'Inactive'}}</div>
      <div 
        v-else
        class="sm-txt-capitalize"
      >{{item.role.replace('_', ' ')}}</div>
    </td>
    <td class="actions">
      <div 
        v-if="!item.is_privileged"
        class="sm-customers-table__desktop-item-actions"
      >
        <div class="sm-customers-table__desktop-item-actions-item">
          <template v-if="!item.is_active">
            <a
              href="#"
              class="icon"
              @click.prevent="subscribeCustomer"
            >
              <hint-wrap :text="`Subscribe ${item.full_name}`">
                <template slot="content">
                  <include-svg :name="'icon-settings-activate'" />
                </template>
              </hint-wrap>
            </a>
          </template>
          <a
            v-else
            href="#"
            class="icon"
            @click.prevent="unsubscribeCustomer"
          >
            <hint-wrap :text="`Cancel Subscription for ${item.full_name}`">
              <template slot="content">
                <include-svg :name="'icon-settings-deactivate'" />
              </template>
            </hint-wrap>
          </a>
        </div>
        <div class="sm-customers-table__desktop-item-actions-item">
          <a
            href="#"
            class="icon"
            @click.prevent="assignPMB"
          >
            <hint-wrap :text="`Assign Mailbox#`">
              <template slot="content">
                <include-svg :name="'icon-pmb'" />
              </template>
            </hint-wrap>
          </a>
        </div>
        <div class="sm-customers-table__desktop-item-actions-item">
          <a
            v-if="item.id != user.id"
            href="#"
            class="icon"
            @click.prevent="loginAsCustomer"
          >
            <hint-wrap :text="`Log in as ${item.full_name}`">
              <template slot="content">
                <include-svg :name="'icon-action-customer'" />
              </template>
            </hint-wrap>
          </a>
        </div>        
      </div>
    </td>
  </tr>
  <div
    v-else
    class="sm-mobile-table-item"
    :class="{
      'open': item.itemOpen,
      'to-approve': showNoDocumentsWarning && item.required_documents_uploaded
    }"
  >
    <div 
      class="sm-mobile-table-item-row"
      @click="toggleItem"
    >
      <div class="sm-mobile-table-item-row-elem name">
        <include-svg :name="'icon-arrow-b-bold'" />
        <a
          href="#"
          class="sm-link-blue"
          :class="{
            'disabled': item.id != userAccount.id && !checkHigherRole(userAccount.role, item.role)
          }"
          @click.stop="(e) => openAccount(item.id, e)"
        >
          <text-truncate :text="item.full_name" />
        </a>
        <hint-wrap
          v-if="warning"
          :text="warning"
          :left="true"
        >
          <template slot="content">
            <!-- <include-svg :name="'icon-warning'" /> -->
            <include-svg 
              :name="showNoDocumentsWarning && item.required_documents_uploaded ? 'icon-documents-approve' : 'icon-warning'"
            />
          </template>
        </hint-wrap>
        <div
          v-if="item.has_joint_accounts"
          class="sm-customers-table__recipients"
          @mouseenter="showRecipientsList"
          @mouseleave="hideRecipientsList"
        >
          <include-svg :name="'icon-joint-accounts'" />
          <hint-block
            v-if="showRecipients"
            :class="'left'"
          >
            <template slot="hintContent">
              <p class="sm-txt-strong">Additional recipients</p>
              <ul
                v-if="item.additional_recipients && item.additional_recipients.length" 
                class="sm-customers-table__recipients-list"
              >
                <li
                  v-for="recip in item.additional_recipients"
                  :key="recip.id"
                >
                  <include-svg :name="'icon-joint-account'" />
                  <span>{{recip.user_name}}</span>
                </li>
              </ul>
              <sm-spinner
                v-else
                :show="true"
              />
            </template>
          </hint-block>
        </div>
      </div>
      <div class="sm-mobile-table-item-row-elem pmb sm-txt-center">
        <span>{{item.pmb}}</span>
        <a
          v-if="item.is_active && item.pmb"
          href="#"
          class="sm-customers-table__quick-mail"
          @click.prevent="quickMail"
        >
          <include-svg 
            :name="'icon-quick-mail'"
          />
        </a>
      </div>
      <div 
        v-if="viewPort == 'medium'"
        class="sm-mobile-table-item-row-elem username"
      >{{item.user_name}}</div>
      <div 
        v-if="viewPort == 'medium'"
        class="sm-mobile-table-item-row-elem plan"
        :class="{
          'term': item.term && showTerm
        }"
      >
        <p>{{item.plan}}</p>
        <span
          v-if="item.term && showTerm"
          class="sm-txt-capitalize sm-small disabled"
        >{{item.term.replace('_', '')}}</span>
      </div>
      <div class="sm-mobile-table-item-row-elem status sm-txt-right">
        <div 
          v-if="!item.is_privileged"
          class="sm-customers-table__mobile-status"
          :class="{'active': item.is_active}"
        >{{item.is_active ? 'Active' : 'Inactive'}}</div>
        <div
          v-else
          class="sm-txt-capitalize"
        >{{item.role.replace('_', ' ')}}</div>
      </div>
      <div
        class="sm-mobile-table-item-row-elem actions" 
        :class="{
          'disabled': item.is_privileged  
        }"
        @click="showPopup"
      >
        <a href="#"></a>
      </div>
    </div>
    <ul class="sm-mobile-table-item-dropdown sm-customers-table__mobile-dropdown">
      <li v-if="viewPort == 'small'">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Name</span>
        <span>{{item.full_name}}</span>
      </li>
      <li v-if="viewPort == 'small' && item.plan">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Plan</span>
        <span>
          <span>{{item.plan}}</span>
          <span 
            v-if="item.term && showTerm"
            class="sm-txt-capitalize"
          > - {{item.term.replace('_', '')}}</span>
        </span>
      </li>
      <li v-if="item.company_name">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Company</span>
        <span>{{item.company_name}}</span>
      </li>
      <li v-if="item.registered_on">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Registered</span>
        <span>{{item.registered_on | moment('MMM D, YYYY')}}</span>
      </li>
      <li v-if="item.started_at">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Started at</span>
        <span>{{item.started_at | moment('MMM D, YYYY')}}</span>
      </li>
      <li v-if="$route.query.tab != 'inactive' && item.renew_on">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Renew on</span>
        <span>{{item.renew_on | moment('MMM D, YYYY')}}</span>
      </li>
      <li v-if="$route.query.tab && $route.query.tab != 'active' && item.ended_at">
        <span class="sm-txt-capitalize sm-customers-table__mobile-dropdown-title">Ended at</span>
        <span>{{item.ended_at | moment('MMM D, YYYY')}}</span>
      </li>
    </ul>
    <div
      class="sm-mobile-table-item-popup"
      v-if="item.popupOpen"
      v-click-outside="hidePopup"
    >
      <ul>
        <li>
          <template v-if="!item.is_active">
            <a
              href="#"
              class="icon"
              @click.prevent="subscribeCustomer"
            >Subscribe {{item.full_name}}</a>
          </template>
          <a
            v-else
            href="#"
            class="icon"
            @click.prevent="unsubscribeCustomer"
          >Cancel Subscription for {{item.full_name}}</a>
        </li>
        <li>
          <a
            href="#"
            class="icon"
            @click.prevent="assignPMB"
          >Assign Mailbox#</a>
        </li>
        <li>
          <a
            v-if="item.id != user.id"
            href="#"
            class="icon"
            @click.prevent="loginAsCustomer"
          >Log in as {{item.full_name}}</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import HintWrap from '@/components/HintWrap';
import Hint from '@/components/Hint';
import {CheckHigherRole} from '@/utils/roles';
import vClickOutside from 'v-click-outside';
import Spinner from '@/components/Spinner';

export default {
  name: 'customers-item-lvl3',
  components: {
    'hint-wrap': HintWrap,
    'hint-block': Hint,
    'sm-spinner': Spinner
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    viewPort: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showTerm: false,
      showRecipients: false,
      recipientsRequested: false,
      hintTimer: null
    }
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      user: 'auth/user',
      userAccount: 'account/userAccount',
      provider: 'provider'
    }),
    showForm1583Warning() {
      return (
        !this.item.is_privileged &&
        this.item.hasOwnProperty('is_form_1583_signed') && 
        !this.item.is_form_1583_signed &&
        this.provider.country_id == 1
      )
    },
    showNoDocumentsWarning() {
      return (
        this.provider.country_id != 1 &&
        !this.item.is_privileged &&
        this.item.hasOwnProperty('account_approval_status') &&
        this.item.account_approval_status != 'approved'
      )
    },
    warning() {
      if (this.showForm1583Warning)
        return 'This user has not signed USPS Form 1583 yet';

      if (this.showNoDocumentsWarning)
        return this.item.required_documents_uploaded ? 'Documents waiting approval' : 'Account is not approved';

      return '';
    }
  },
  methods: {
    checkHigherRole: CheckHigherRole,
    toggleItem(e) {
      if (!e.target.classList.contains('actions'))
        this.item.itemOpen = !this.item.itemOpen;
    },
    assignPMB() {
      this.$emit('action', {
        name: 'pmb',
        id: this.item.id
      });
    },
    loginAsCustomer() {
      this.$emit('action', {
        name: 'login',
        id: this.item.id
      });
    },
    subscribeCustomer() {
      this.$router.push(`/admin/activate-customer/${this.item.id}`);
    },
    unsubscribeCustomer() {
      this.$emit('action', {
        name: 'unsubscribe',
        id: this.item.id
      });
    },
    quickMail(e) {
      e.stopPropagation();

      this.$emit('action', {
        name: 'quick-mail',
        pmb: this.item.pmb
      });
    },
    showPopup() {
      this.item.popupOpen = true;
    },
    hidePopup() {
      this.item.popupOpen = false;
    },
    openAccount(id, e, documents) {
      if (id == this.userAccount.id) {
        this.$router.push('account');
      } else {
        this.$router.push(`customer/${id}${documents ? '?from=list' : ''}`);
      }

      if (e)
        e.preventDefault();
    },
    showRecipientsList() {
      this.hintTimer = setTimeout(() => this.showRecipients = true, 10);

      if (!this.recipientsRequested) {
        this.recipientsRequested = true;
        this.$emit('action', {
          name: 'recipients',
          customer: this.item
        });
      }
    },
    hideRecipientsList() {
      this.showRecipients = false;
      clearTimeout(this.hintTimer);
    }
  },
  directives: {
    clickOutside: vClickOutside.directive
  }
}
</script>